/* Global call orchestrator.
 *
 * Owns ONE active call across the whole app (outgoing or incoming). Listens
 * to messaging SSE events for call:incoming / call:answered / call:rejected
 * / call:canceled / call:ended, and drives the Agora client via
 * `useVoiceCall`. UI layer renders based on the reducer-style state
 * exposed here. */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "./UserAuthContext";
import { useMessagingStream } from "./MessagingStreamContext";
import { useVoiceCall } from "../hooks/useVoiceCall";
import { VOICE_CALLS_ENABLED } from "../lib/featureFlags";
import { PHASE } from "./callContext/_phase";
import { doStartCall, doAccept, doDecline, doHangup } from "./callContext/_actions";
import {
  usePrewarmAgora, routeMessagingEvent, useUrlCallHydration,
  useSwNavBridge, useRingingSafetyNet, useHangupPoll, useRemotePeerLeft,
} from "./callContext/_effects";

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { user } = useUser();
  const voice = useVoiceCall();

  const [phase, setPhase] = useState(PHASE.IDLE);
  const [activeCall, setActiveCall] = useState(null);
  const [peer, setPeer] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [error, setError] = useState(null);

  const activeCallRef = useRef(null);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

  // Agora pre-warm is skipped when the voice-calls feature is disabled
  // (Feb 2026, pending iOS PushKit + CallKit integration). Saves loading
  // the ~150 KB Agora RTC SDK on boot for every user.
  usePrewarmAgora(VOICE_CALLS_ENABLED ? user?.id : null);

  const reset = useCallback(async () => {
    try { await voice.leave(); } catch { /* noop */ }
    setPhase(PHASE.IDLE);
    setActiveCall(null);
    setPeer(null);
    setCallStartedAt(null);
    setError(null);
  }, [voice]);

  const startCall = useCallback(
    ({ conversationId, calleeId, calleeProfile }) => {
      // Safety net: even if a stale/cached UI element manages to call
      // this, we bail out silently when the feature is disabled.
      if (!VOICE_CALLS_ENABLED) return Promise.resolve();
      return doStartCall({
        conversationId, calleeId, calleeProfile, phase, voice,
        setError, setPhase, setPeer, setActiveCall, reset,
      });
    },
    [phase, voice, reset],
  );

  const acceptIncoming = useCallback(
    () => doAccept({
      call: activeCallRef.current, phase, voice,
      setPhase, setCallStartedAt, setError, reset,
    }),
    [phase, voice, reset],
  );

  const declineIncoming = useCallback(
    () => doDecline(activeCallRef.current, reset),
    [reset],
  );

  const hangup = useCallback(
    () => doHangup(activeCallRef.current, phase, reset),
    [phase, reset],
  );

  // SSE — listen to call signaling via the SHARED stream. When the
  // voice-calls feature is disabled we still subscribe (a no-op handler)
  // to keep the hook order stable, but ignore every call:* event so no
  // ringing UI can appear even if the backend sends one.
  useMessagingStream((ev) => {
    if (!VOICE_CALLS_ENABLED) return;
    routeMessagingEvent(ev, {
      userId: user?.id, activeCallRef, setActiveCall, setPeer, setPhase, setCallStartedAt, reset,
    });
  });

  const location = useLocation();
  const navigate = useNavigate();
  // These effects hydrate call state from URL / SW / poll — pointless
  // when voice is disabled, so we pass `null` user/id to make them exit
  // early without breaking hook order.
  const voiceUser = VOICE_CALLS_ENABLED ? user : null;
  const voiceActiveCallId = VOICE_CALLS_ENABLED ? activeCall?.id : null;
  useUrlCallHydration({ user: voiceUser, location, activeCallRef, setActiveCall, setPeer, setPhase });
  useSwNavBridge(navigate);
  useRingingSafetyNet({ user: voiceUser, activeCallRef, setActiveCall, setPeer, setPhase });
  useHangupPoll({ activeCallId: voiceActiveCallId, phase, reset });
  useRemotePeerLeft(phase, reset);

  const value = useMemo(
    () => ({
      phase, activeCall, peer, callStartedAt,
      remoteUsers: voice.remoteUsers,
      isMuted: voice.isMuted,
      isSpeakerOn: voice.isSpeakerOn,
      error,
      startCall, acceptIncoming, declineIncoming, hangup,
      toggleMute: voice.toggleMute,
      toggleSpeaker: voice.toggleSpeaker,
    }),
    [phase, activeCall, peer, callStartedAt, voice.remoteUsers, voice.isMuted,
     voice.isSpeakerOn, error, startCall, acceptIncoming, declineIncoming, hangup,
     voice.toggleMute, voice.toggleSpeaker],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export const useCall = () => {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used inside <CallProvider>");
  return ctx;
};

export const CALL_PHASE = PHASE;
